import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import { sinSelloConMotivoLegible } from "./parametros/parametros.util";
import { Base } from "src/app/shared/model/core/base.model";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import {
  AsistenciaConfig,
  AsistenciaDia,
  Dispositivo,
  EmpleadoModalidad,
  Estacion,
  ModalidadRenglon,
  MonitoreoFiltro,
  MonitoreoRenglon,
  PlanDia,
  Ubicacion,
} from "src/app/shared/model/asistencia/asistencia.model";

/**
 * Puerta unica del modulo de asistencia hacia el backend.
 *
 * Ningun metodo manda la empresa: el servidor la resuelve desde la
 * sesion y pisa lo que llegue del cuerpo. Mandarla desde aqui daria la
 * falsa impresion de que el cliente la elige.
 *
 * Los endpoints de monitoreo y de plan comparten filtro a proposito: son
 * el mismo desde/hasta con un empleado opcional, y tener dos tipos
 * distintos solo llevaria a que uno de los dos se quedara atras.
 */
@Injectable({
  providedIn: "root",
})
export class AsistenciaService {
  constructor(private http: HttpClient) {}

  // ------------------------------------------------------------------
  // Lo mio
  // ------------------------------------------------------------------

  /** Mi dia: jornada esperada, lo registrado y el veredicto. */
  miDia(fecha: string | null): Observable<AsistenciaDia> {
    const peticion: any = {};
    if (fecha) {
      peticion.fecha = fecha;
    }
    return this.http.post<AsistenciaDia>(UrlConstantes.ASISTENCIA_MI_DIA, peticion);
  }

  /** Lo declarado en un rango. Sin idEmpleado devuelve lo propio. */
  consultarPlan(filtro: MonitoreoFiltro): Observable<ConsultaList<PlanDia>> {
    return this.http.post<ConsultaList<PlanDia>>(UrlConstantes.ASISTENCIA_MI_PLAN, filtro);
  }

  /**
   * Declara donde se trabaja un dia. Si el plan trae idEmpleado y no es
   * el de la sesion, el servidor lo registra como declarado por un lider.
   */
  declararPlan(plan: PlanDia): Observable<PlanDia> {
    return this.http.post<PlanDia>(UrlConstantes.ASISTENCIA_DECLARAR_PLAN, plan);
  }

  borrarPlan(plan: PlanDia): Observable<Base> {
    return this.http.post<Base>(UrlConstantes.ASISTENCIA_BORRAR_PLAN, plan);
  }

  // ------------------------------------------------------------------
  // Monitoreo
  // ------------------------------------------------------------------

  consultarMonitoreo(filtro: MonitoreoFiltro): Observable<ConsultaList<MonitoreoRenglon>> {
    return this.http.post<ConsultaList<MonitoreoRenglon>>(UrlConstantes.ASISTENCIA_MONITOREO, filtro);
  }

  /** Cuantos hay de cada estatus en el mismo rango. */
  resumenMonitoreo(filtro: MonitoreoFiltro): Observable<{ [estatus: string]: number }> {
    return this.http.post<{ [estatus: string]: number }>(
      UrlConstantes.ASISTENCIA_MONITOREO_RESUMEN,
      filtro
    );
  }

  // ------------------------------------------------------------------
  // Sitios
  // ------------------------------------------------------------------

  consultarUbicaciones(filtro: Ubicacion | null): Observable<ConsultaList<Ubicacion>> {
    return this.http.post<ConsultaList<Ubicacion>>(
      UrlConstantes.ASISTENCIA_UBICACION_CONSULTA,
      filtro ?? {}
    );
  }

  guardarUbicacion(ubicacion: Ubicacion): Observable<Ubicacion> {
    return this.http.post<Ubicacion>(UrlConstantes.ASISTENCIA_UBICACION_GUARDAR, ubicacion);
  }

  desactivarUbicacion(ubicacion: Ubicacion): Observable<Base> {
    return this.http.post<Base>(UrlConstantes.ASISTENCIA_UBICACION_DESACTIVAR, ubicacion);
  }

  reactivarUbicacion(ubicacion: Ubicacion): Observable<Base> {
    return this.http.post<Base>(UrlConstantes.ASISTENCIA_UBICACION_REACTIVAR, ubicacion);
  }

  // ------------------------------------------------------------------
  // Modalidad del empleado
  // ------------------------------------------------------------------

  consultarModalidades(idEmpleado: number): Observable<ConsultaList<EmpleadoModalidad>> {
    const filtro = new EmpleadoModalidad();
    filtro.idEmpleado = idEmpleado;
    return this.http.post<ConsultaList<EmpleadoModalidad>>(
      UrlConstantes.ASISTENCIA_MODALIDAD_CONSULTA,
      filtro
    );
  }

  /**
   * La plantilla de la empresa con su modalidad vigente.
   *
   * Va aparte de consultarModalidades a proposito: aquella da el historial
   * de UNA persona, y esta da a TODAS, incluida la que no tiene modalidad
   * asignada. Sin esta ultima la pantalla no podria responder la unica
   * pregunta que de verdad importa al desplegar el modulo: a quien le falta.
   */
  consultarPlantillaModalidad(filtro: MonitoreoFiltro): Observable<ConsultaList<ModalidadRenglon>> {
    return this.http.post<ConsultaList<ModalidadRenglon>>(
      UrlConstantes.ASISTENCIA_MODALIDAD_PLANTILLA,
      filtro
    );
  }

  asignarModalidad(modalidad: EmpleadoModalidad): Observable<EmpleadoModalidad> {
    return this.http.post<EmpleadoModalidad>(
      UrlConstantes.ASISTENCIA_MODALIDAD_ASIGNAR,
      modalidad
    );
  }

  // ------------------------------------------------------------------
  // Estaciones de kiosco
  // ------------------------------------------------------------------

  consultarEstaciones(): Observable<ConsultaList<Estacion>> {
    return this.http.post<ConsultaList<Estacion>>(UrlConstantes.ASISTENCIA_ESTACION_CONSULTA, {});
  }

  /**
   * Emite la credencial de una estacion.
   *
   * La respuesta trae tokenEnClaro y es la UNICA vez que existe: en base
   * solo queda su hash. Si el operador no lo copia, hay que emitir otra.
   */
  emitirEstacion(estacion: Estacion): Observable<Estacion> {
    return this.http.post<Estacion>(UrlConstantes.ASISTENCIA_ESTACION_EMITIR, estacion);
  }

  revocarEstacion(estacion: Estacion): Observable<Estacion> {
    return this.http.post<Estacion>(UrlConstantes.ASISTENCIA_ESTACION_REVOCAR, estacion);
  }

  // ------------------------------------------------------------------
  // Parametros de la empresa
  // ------------------------------------------------------------------

  /**
   * Los parametros con los que se evalua. Si la empresa nunca se
   * configuro, el servidor devuelve los valores por omision para que la
   * pantalla abra con algo sensato en vez de con campos vacios.
   *
   * El rechazo llega sellado en las cabeceras (SelloDeError) y con un
   * bean de valores por omision en el cuerpo, que por si solo parece una
   * respuesta buena: por eso se leen las cabeceras y sale por `error:`
   * con el motivo del servidor. El motivo sale SOLO de MESSAGE
   * (sinSelloConMotivoLegible): un fallo inesperado trae en IDMESSAGE el
   * texto técnico en inglés de la excepción, y ése no se le enseña a nadie.
   */
  consultarConfig(): Observable<AsistenciaConfig> {
    return this.http
      .post<AsistenciaConfig>(UrlConstantes.ASISTENCIA_CONFIG_CONSULTA, {}, { observe: "response" })
      .pipe(sinSelloConMotivoLegible<AsistenciaConfig>("No se pudieron consultar los parámetros."));
  }

  /**
   * Igual que la consulta: sin leer las cabeceras, un guardado rechazado
   * se veia como exito, porque el bean de error trae reglaFalta por omision.
   */
  guardarConfig(config: AsistenciaConfig): Observable<AsistenciaConfig> {
    return this.http
      .post<AsistenciaConfig>(UrlConstantes.ASISTENCIA_CONFIG_GUARDAR, config, { observe: "response" })
      .pipe(sinSelloConMotivoLegible<AsistenciaConfig>("No se pudieron guardar los parámetros."));
  }

  // ------------------------------------------------------------------
  // Telefonos vinculados
  // ------------------------------------------------------------------

  consultarDispositivos(idEmpleado: number | null): Observable<ConsultaList<Dispositivo>> {
    const filtro = new Dispositivo();
    if (idEmpleado) {
      filtro.idEmpleado = idEmpleado;
    }
    return this.http.post<ConsultaList<Dispositivo>>(
      UrlConstantes.ASISTENCIA_DISPOSITIVO_CONSULTA,
      filtro
    );
  }

  /**
   * La bandeja de aprobacion: los equipos de TODA la empresa.
   *
   * consultarDispositivos no sirve para esto: sin idEmpleado devuelve los
   * del usuario que consulta, que es lo correcto para la app del empleado y
   * lo inservible para quien administra. Y quien administra no sabe de
   * antemano QUIEN se dio de alta con un telefono nuevo; esa es la pregunta.
   *
   * @param estatus PENDIENTE es como se abre la pantalla; null los trae todos.
   */
  consultarBandejaDispositivos(estatus: string | null): Observable<ConsultaList<Dispositivo>> {
    const filtro = new Dispositivo();
    if (estatus) {
      filtro.estatus = estatus;
    }
    return this.http.post<ConsultaList<Dispositivo>>(
      UrlConstantes.ASISTENCIA_DISPOSITIVO_BANDEJA,
      filtro
    );
  }

  aprobarDispositivo(dispositivo: Dispositivo): Observable<Base> {
    return this.http.post<Base>(UrlConstantes.ASISTENCIA_DISPOSITIVO_APROBAR, dispositivo);
  }

  revocarDispositivo(dispositivo: Dispositivo): Observable<Base> {
    return this.http.post<Base>(UrlConstantes.ASISTENCIA_DISPOSITIVO_REVOCAR, dispositivo);
  }
}
